import { Local } from "./storage"

const TOKEN_KEY = 'ny_pc_token'
const ROLE_KEY = 'ny_pc_role'
const MENUS_KEY = 'ny_pc_menus'
const LOGIN_PAYLOAD_KEY = 'ny_pc_login_payload'

function normalizeTenantIdValue(value) {
  if (value == null || value === '') return value
  return String(value)
}

function decodeJwtPayloadText(token) {
  if (!token || typeof token !== 'string') return ''
  const parts = token.split('.')
  if (parts.length < 2 || !parts[1]) return ''
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const normalized = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    return decodeURIComponent(
      Array.from(atob(normalized))
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    )
  } catch {
    return ''
  }
}

function readTenantIdFromPayloadText(payloadText, fieldNames) {
  if (!payloadText) return ''
  for (const fieldName of fieldNames) {
    const match = payloadText.match(new RegExp(`"${fieldName}"\\s*:\\s*("([^"]+)"|(\\d+))`))
    if (!match) continue
    const value = match[2] ?? match[3] ?? ''
    if (value) return String(value)
  }
  return ''
}

function normalizeLoginPayload(payload) {
  if (!payload || typeof payload !== 'object') return {}
  const safePayload = { ...payload }

  safePayload.tenantId = normalizeTenantIdValue(safePayload.tenantId)
  safePayload.currentTenantId = normalizeTenantIdValue(safePayload.currentTenantId)
  safePayload.rootTenantId = normalizeTenantIdValue(safePayload.rootTenantId)

  if (safePayload.auth && typeof safePayload.auth === 'object') {
    safePayload.auth = {
      ...safePayload.auth,
      tenantId: normalizeTenantIdValue(safePayload.auth.tenantId),
      currentTenantId: normalizeTenantIdValue(safePayload.auth.currentTenantId),
      rootTenantId: normalizeTenantIdValue(safePayload.auth.rootTenantId),
    }
  }

  if (safePayload.user && typeof safePayload.user === 'object') {
    safePayload.user = {
      ...safePayload.user,
      tenantId: normalizeTenantIdValue(safePayload.user.tenantId),
      currentTenantId: normalizeTenantIdValue(safePayload.user.currentTenantId),
      rootTenantId: normalizeTenantIdValue(safePayload.user.rootTenantId),
    }
  }

  if (safePayload.data && typeof safePayload.data === 'object') {
    safePayload.data = {
      ...safePayload.data,
      tenantId: normalizeTenantIdValue(safePayload.data.tenantId),
      currentTenantId: normalizeTenantIdValue(safePayload.data.currentTenantId),
      rootTenantId: normalizeTenantIdValue(safePayload.data.rootTenantId),
    }
  }

  return safePayload
}

export function getAuthToken() {
  return Local.get(TOKEN_KEY) || ''
}

export function setAuthToken(token) {
  Local.set(TOKEN_KEY, token)
}

export function clearAuthToken() {
  Local.remove(TOKEN_KEY)
}

export function getStoredRole() {
  return Local.get(ROLE_KEY) || ''
}

export function setStoredRole(role) {
  Local.set(ROLE_KEY, role)
}

export function clearStoredRole() {
  Local.remove(ROLE_KEY)
}

export function getStoredMenus() {
  const raw = Local.get(MENUS_KEY)
  return raw ?? []
}

export function setStoredMenus(menus) {
  const safeMenus = Array.isArray(menus) ? menus : []
  Local.set(MENUS_KEY, safeMenus)
}

export function clearStoredMenus() {
  Local.remove(MENUS_KEY)
}

export function getStoredLoginPayload() {
  const raw = Local.get(LOGIN_PAYLOAD_KEY)
  return raw ?? []
}

export function getStoredUserName() {
  const payload = getStoredLoginPayload()
  if (!payload || typeof payload !== 'object') return ''
  const candidates = [
    payload.nickname,
    payload.realName,
    payload.name,
    payload.userName,
    payload.username,
    payload.account,
    payload.user?.nickname,
    payload.user?.realName,
    payload.user?.name,
    payload.user?.userName,
    payload.user?.username,
    payload.auth?.nickname,
    payload.auth?.realName,
    payload.auth?.name,
    payload.auth?.userName,
    payload.auth?.username,
    payload.auth?.account,
    payload.data?.nickname,
    payload.data?.realName,
    payload.data?.name,
    payload.data?.userName,
    payload.data?.username,
    payload.data?.account,
  ]
  for (const value of candidates) {
    if (value == null) continue
    const text = String(value).trim()
    if (text) return text
  }
  return ''
}

export function getCurrentTenantId() {
  const token = getAuthToken()
  const payloadText = decodeJwtPayloadText(token)
  const tokenTenantId = readTenantIdFromPayloadText(payloadText, [
    'tenantId',
    'currentTenantId',
    'rootTenantId',
  ])
  if (tokenTenantId) return tokenTenantId

  const payload = getStoredLoginPayload()
  if (!payload || typeof payload !== 'object') return ''
  const candidates = [
    payload.tenantId,
    payload.currentTenantId,
    payload.auth?.tenantId,
    payload.user?.tenantId,
    payload.data?.tenantId,
    payload.rootTenantId,
    payload.auth?.currentTenantId,
    payload.user?.currentTenantId,
    payload.data?.currentTenantId,
  ]
  for (const value of candidates) {
    if (value == null || value === '') continue
    return String(value)
  }
  return ''
}

export function setStoredLoginPayload(payload) {
  const safePayload = normalizeLoginPayload(payload)
  Local.set(LOGIN_PAYLOAD_KEY, safePayload)
}

export function clearStoredLoginPayload() {
  Local.remove(LOGIN_PAYLOAD_KEY)
}

export function logoutAndRedirect() {
  clearAuthToken()
  clearStoredRole()
  clearStoredMenus()
  clearStoredLoginPayload()
  Local.remove('user_menus')
  Local.remove('access_token')
  Local.remove('user_id')
  Local.remove('user_phone')
  Local.remove('user_headImg')
  Local.remove('userInfo')
  Local.remove('tenantInfo')
  Local.remove('isFirst')

  if (typeof window !== 'undefined') {
    if (window.location.pathname !== '/login') {
      window.location.replace('/login')
    } else {
      window.location.reload()
    }
  }
}

function normalizeMenuPath(path) {
  const text = String(path || '').trim()
  if (!text) return ''
  const withSlash = text.startsWith('/') ? text : `/${text}`
  return withSlash.replace(/\/+$/, '') || '/'
}

function normalizeRoleKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-')
}

export function resolveRoleKey(payload) {
  if (!payload || typeof payload !== 'object') return ''
  const roleValues = (roles) => {
    if (!Array.isArray(roles)) return []
    return roles.flatMap((item) => {
      if (!item || typeof item !== 'object') return [item]
      return [item.roleCode, item.roleName, item.code, item.name, item.authorityKey]
    })
  }
  const candidates = [
    ...roleValues(payload?.auth?.roles),
    ...roleValues(payload?.roles),
    ...roleValues(payload?.user?.roles),
    ...roleValues(payload?.data?.roles),
    payload?.auth?.role,
    payload?.auth?.roleCode,
    payload?.auth?.roleName,
    payload?.role,
    payload?.roleCode,
    payload?.roleName,
    payload?.user?.roleCode,
    payload?.user?.roleName,
    payload?.data?.roleCode,
    payload?.data?.roleName,
  ]
  for (const candidate of candidates) {
    const text = String(candidate || '').trim()
    if (!text) continue
    const normalized = normalizeRoleKey(text)
    if (['sp-emp', 'sp-admin', 'ny-emp', 'ny-admin', 'super'].includes(normalized)) {
      return normalized
    }
    if (normalized.includes('super') || normalized.includes('超级')) {
      return 'super'
    }
    if (normalized.includes('ny') || normalized.includes('南粤')) {
      return normalized.includes('admin') ? 'ny-admin' : 'ny-emp'
    }
    if (normalized.includes('sp') || normalized.includes('服务商') || normalized.includes('浙汇')) {
      return normalized.includes('admin') ? 'sp-admin' : 'sp-emp'
    }
  }
  return ''
}

const MENU_ROUTE_ALIASES = {
  operations_dashboard: '/operations/dashboard',
  'operations-dashboard': '/operations/dashboard',
  '/operations/dashboard': '/operations/dashboard',
  ny_dashboard: '/operations/dashboard',
  'ny-dashboard': '/operations/dashboard',
  '/ny-dashboard': '/operations/dashboard',
  NYOperationsDashboardPage: '/operations/dashboard',
  运营工作台: '/operations/dashboard',
  settlement: '/settlement',
  '/settlement': '/settlement',
  分账管理: '/settlement',
  service_dispatch: '/service-dispatch',
  'service-dispatch': '/service-dispatch',
  '/service-dispatch': '/service-dispatch',
  分配调度: '/service-dispatch',
  ny_report: '/ny-report',
  'ny-report': '/ny-report',
  '/ny-report': '/ny-report',
  报表: '/ny-report',
  报表管理: '/ny-report',
  rules: '/rules',
  '/rules': '/rules',
  规则配置: '/rules',
  sla_monitor: '/sla-monitor',
  'sla-monitor': '/sla-monitor',
  '/sla-monitor': '/sla-monitor',
  SLA监督: '/sla-monitor',
  'SLA 监督': '/sla-monitor',
  sp_deadlines: '/sp-deadlines',
  'sp-deadlines': '/sp-deadlines',
  '/sp-deadlines': '/sp-deadlines',
  时效监控: '/sp-deadlines',
  sp_orders: '/sp-orders',
  'sp-orders': '/sp-orders',
  '/sp-orders': '/sp-orders',
  服务单监控: '/sp-orders',
}

const ROLE_FIRST_PATHS = {
  'sp-emp': '/customers',
  'sp-admin': '/leadpool',
  'ny-emp': '/my-leads',
  'ny-admin': '/operations/dashboard',
  super: '/products',
}

export function getRoleFirstPath(role) {
  return ROLE_FIRST_PATHS[role] || ROLE_FIRST_PATHS['sp-emp']
}

export function getBoundMenuPath(item) {
  if (!item || typeof item !== 'object') return ''
  const candidates = [
    item.path,
    item.routePath,
    item.route_path,
    item.componentPath,
    item.component_path,
    item.component,
    item.url,
    item.resourceCode,
    item.authorityKey,
    item.menuCode,
    item.key,
    item.menuName,
    item.resourceName,
    item.name,
  ]
  for (const value of candidates) {
    const text = String(value || '').trim()
    if (!text) continue
    const normalized = normalizeMenuPath(text)
    const alias = MENU_ROUTE_ALIASES[text] || MENU_ROUTE_ALIASES[normalized]
    if (alias) return alias
  }
  return ''
}

function getMenuPath(item) {
  const boundPath = getBoundMenuPath(item)
  if (boundPath) return boundPath
  return normalizeMenuPath(
    item?.path ||
      item?.routePath ||
      item?.route_path ||
      item?.componentPath ||
      item?.component_path ||
      item?.url ||
      item?.resourceCode ||
      item?.authorityKey ||
      item?.menuCode ||
      item?.key,
  )
}

function isMenuContainer(item, path) {
  const type = String(item?.type || item?.resourceType || item?.menuType || '').toUpperCase()
  return (
    type === 'DIR' ||
    type === 'DIRECTORY' ||
    type === 'FOLDER' ||
    type === 'BUTTON' ||
    path === '/dashboard' ||
    path.includes(':')
  )
}

export function getFirstAccessibleMenuPath(menus) {
  if (!Array.isArray(menus)) return ''
  for (const item of menus) {
    if (!item || typeof item !== 'object') continue
    const childPath = getFirstAccessibleMenuPath(item.children)
    if (childPath) return childPath
    const path = getMenuPath(item)
    if (path && !isMenuContainer(item, path)) return path
  }
  return ''
}
